import OpenAI from 'openai';
import { 
  getOptimalModel, 
  getModelConfig,
  type TaskType
} from '../../config/openai/models';
import { openai } from '../openai/config';
import { ApiError } from '../errors';

/**
 * Enhanced OpenAI client with intelligent model routing
 * Automatically selects the best model for each task type
 */
export class ModelRouter {
  private client: OpenAI;

  constructor() {
    this.client = openai;
  }

  /**
   * Create a chat completion with automatic model selection
   */
  async createChatCompletion(
    taskType: TaskType,
    messages: OpenAI.Chat.ChatCompletionMessageParam[],
    options: Partial<OpenAI.Chat.ChatCompletionCreateParams> = {}
  ): Promise<OpenAI.Chat.ChatCompletion> {
    const model = getOptimalModel(taskType);
    const modelConfig = getModelConfig(model);

    // Merge default model config with user options
    const params: any = {
      model,
      messages,
      ...options
    };

    // Add temperature only if supported by the model
    if (!(modelConfig as any).disableTemperature) {
      params.temperature = options.temperature ?? modelConfig.temperature;
    }

    // Add penalties only if supported by the model
    if (!(modelConfig as any).disablePenalties) {
      params.presence_penalty = options.presence_penalty ?? modelConfig.presencePenalty;
      params.frequency_penalty = options.frequency_penalty ?? modelConfig.frequencyPenalty;
    }

    // Handle different token parameter names for different models
    const maxTokens = options.max_tokens ?? modelConfig.maxTokens;
    if ((modelConfig as any).useMaxCompletionTokens) {
      params.max_completion_tokens = maxTokens;
    } else {
      params.max_tokens = maxTokens;
    }

    try {
      const response = await this.client.chat.completions.create(params) as OpenAI.Chat.ChatCompletion;
      
      if (!response?.choices?.length) {
        throw new ApiError('Invalid response from OpenAI', 500);
      }

      return response;
    } catch (error) {
      if (error instanceof OpenAI.APIError) {
        switch (error.status) {
          case 400:
            throw new ApiError('Invalid request to OpenAI API: ' + error.message, 400);
          case 401:
            throw new ApiError('Invalid OpenAI API key', 401);
          case 429:
            throw new ApiError('OpenAI API rate limit exceeded. Please try again later.', 429);
          case 500:
            throw new ApiError('OpenAI service error. Please try again later.', 500);
          default:
            throw new ApiError('OpenAI API error: ' + error.message, error.status || 500);
        }
      }

      if (error instanceof Error) {
        throw new ApiError('Failed to communicate with OpenAI: ' + error.message, 500);
      }

      throw new ApiError('An unexpected error occurred during AI processing', 500);
    }
  }

  /**
   * Get the content from a chat completion response
   */
  extractContent(response: OpenAI.Chat.ChatCompletion): string {
    const content = response.choices[0]?.message?.content;
    if (!content) {
      throw new ApiError('Empty response from OpenAI', 500);
    }
    return content;
  }

  /**
   * Parse JSON response with error handling
   */
  parseJsonResponse(content: string): any {
    // Clean the response to ensure it's valid JSON (remove markdown formatting)
    const cleanedContent = content.replace(/```json\n?/g, '').replace(/```\n?/g, '').trim();
    
    try {
      return JSON.parse(cleanedContent);
    } catch (parseError) {
      console.error('Failed to parse AI JSON response:', cleanedContent);
      throw new ApiError('Invalid JSON response from AI', 500);
    }
  }

  /**
   * Get the optimal model for a task type (for direct access)
   */
  getModelForTask(taskType: TaskType): string {
    return getOptimalModel(taskType);
  }

  /**
   * Estimate cost for a task
   */
  estimateCost(taskType: TaskType, inputTokens: number, outputTokens: number): number {
    const model = getOptimalModel(taskType);
    const config = getModelConfig(model);
    const inputCost = (inputTokens / 1_000_000) * config.cost.input;
    const outputCost = (outputTokens / 1_000_000) * config.cost.output;
    return inputCost + outputCost;
  }
}

// Export singleton instance
export const modelRouter = new ModelRouter();

// Export convenience functions
export const createChatCompletion = modelRouter.createChatCompletion.bind(modelRouter);
export const extractContent = modelRouter.extractContent.bind(modelRouter);
export const parseJsonResponse = modelRouter.parseJsonResponse.bind(modelRouter);
export const getModelForTask = modelRouter.getModelForTask.bind(modelRouter);
export const estimateCost = modelRouter.estimateCost.bind(modelRouter); 